/**
 * Invitation à installer le jeu sur l'écran d'accueil.
 *
 * **Pourquoi ce fichier existe : Apple n'a pas d'API d'installation.** Sur Android,
 * Chromium émet `beforeinstallprompt` et le site peut ouvrir la boîte d'installation
 * lui-même. Sur iOS, il n'existe **rien** d'équivalent — ni événement, ni méthode, ni
 * bannière automatique. L'installation passe obligatoirement par le geste
 * *Partager → Sur l'écran d'accueil*, et un site qui ne l'explique pas laisse un joueur
 * iPhone sans aucun moyen de découvrir qu'il peut installer le jeu. C'est exactement ce
 * qui se passait : les balises `apple-touch-icon` et `apple-mobile-web-app-*` étaient
 * bien posées dans `index.html`, donc l'installation MARCHAIT — mais personne ne pouvait
 * le savoir.
 *
 * Conséquence de conception : la même ligne de réglages dit trois choses différentes
 * selon la plateforme, et **n'apparaît pas du tout** quand elle n'a rien à proposer. Une
 * ligne « Installer » inerte sur un ordinateur de bureau serait pire que son absence.
 *
 * ⚠️ L'écoute de `beforeinstallprompt` est posée à l'IMPORT, pas dans une fonction.
 * Chromium émet l'événement une fois, peu après le chargement ; un écouteur installé au
 * premier clic sur les réglages arriverait toujours trop tard, et la boîte native ne
 * pourrait plus être ouverte.
 */

import { t } from '@/i18n';
import { createButton } from './components';
import { icon } from './icons';

/**
 * L'événement d'installation de Chromium, absent des types du DOM standard parce qu'il
 * n'est pas dans la spécification — c'est une extension propre aux navigateurs Chromium.
 */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>;
}

let captured: InstallPromptEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Sans `preventDefault`, certains navigateurs affichent leur propre mini-bandeau en
    // plus du nôtre : deux invitations pour une seule action.
    event.preventDefault();
    captured = event as InstallPromptEvent;
  });
}

/** Le jeu tourne-t-il déjà comme une application installée ? */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // `navigator.standalone` est la variante iOS, absente des types standard.
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return window.matchMedia('(display-mode: standalone)').matches || iosStandalone;
}

/**
 * Est-on sur un appareil Apple mobile ?
 *
 * ⚠️ `iPad` ne suffit PAS. Depuis iPadOS 13, Safari sur iPad s'annonce comme un Mac :
 * l'agent utilisateur contient `Macintosh`, et un test sur « iPad » rate donc toutes les
 * tablettes récentes. La signature fiable est un Mac **tactile** — un vrai Mac rend
 * `maxTouchPoints` à 0.
 */
export function isIosLike(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return true;
  return navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1;
}

export type InstallState = 'standalone' | 'prompt' | 'ios' | 'unavailable';

export function installState(): InstallState {
  if (isStandalone()) return 'standalone';
  if (captured !== null) return 'prompt';
  if (isIosLike()) return 'ios';
  return 'unavailable';
}

export interface InstallRowHandle {
  readonly el: HTMLElement;
  /** Réévalue l'état : l'événement Chromium peut arriver après l'ouverture des réglages. */
  refresh(): void;
}

/**
 * Ligne de réglages « Installer l'application ».
 *
 * Elle se masque d'elle-même quand il n'y a rien à proposer, et se réévalue à chaque
 * ouverture des réglages : `beforeinstallprompt` peut très bien arriver après.
 */
export function createInstallRow(): InstallRowHandle {
  const el = document.createElement('div');
  el.className = 'install-row';

  const head = document.createElement('div');
  head.className = 'install-row__head';
  head.append(icon('plus'));

  const title = document.createElement('b');
  title.textContent = t('install.title');
  head.append(title);

  const text = document.createElement('p');
  text.className = 'install-row__text';

  const action = createButton({
    label: t('install.action'),
    variant: 'secondary',
    size: 'sm',
    onClick: () => {
      if (captured === null) return;
      void captured.prompt().finally(() => {
        // L'événement ne se rejoue pas : une fois consommé, la boîte native ne peut plus
        // être rouverte par le site. On retombe donc sur l'état suivant.
        captured = null;
        refresh();
      });
    },
  });

  el.append(head, text, action.el);

  function refresh(): void {
    const state = installState();

    el.hidden = state === 'unavailable';
    action.el.hidden = state !== 'prompt';

    if (state === 'standalone') {
      text.textContent = t('install.done');
    } else if (state === 'ios') {
      // Le geste, en toutes lettres : c'est la seule voie sur iOS.
      text.textContent = t('install.ios');
    } else {
      text.textContent = t('install.why');
    }
  }

  refresh();

  return { el, refresh };
}

/* ── Bannière d'ouverture ──────────────────────────────────────────────────── */

/**
 * Depuis quand l'invitation a-t-elle été écartée, et pour combien de temps.
 *
 * Une invitation qu'on écarte doit rester écartée, sinon elle devient une nuisance ; mais
 * la faire disparaître à jamais sur un seul geste distrait prive le joueur de la
 * fonctionnalité. Sept jours : assez long pour ne pas harceler, assez court pour que
 * quelqu'un qui prend goût au jeu la retrouve.
 */
const DISMISS_KEY = 'villopia:install-dismissed-at';
const DISMISS_DAYS = 7;

/**
 * Délai avant l'apparition, en millisecondes.
 *
 * E§4 veut la ville visible à l'arrivée : proposer d'installer une application qu'on n'a
 * pas encore vue tourner est la meilleure façon de se faire refuser. On laisse donc jouer
 * d'abord.
 */
const APPEAR_AFTER = 25_000;

function dismissedRecently(now: number): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (raw === null) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return now - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    // Navigation privée ou stockage plein : on préfère proposer une fois de trop que de
    // faire échouer le démarrage du jeu pour une bannière.
    return false;
  }
}

export interface InstallBannerHandle {
  readonly el: HTMLElement;
  /** Programme l'apparition si l'état s'y prête. À appeler une fois au démarrage. */
  schedule(now: number): void;
  destroy(): void;
}

/**
 * Bannière discrète invitant à installer le jeu.
 *
 * **C'est le seul chemin sur iOS**, où aucune boîte native n'existe : ce que les autres
 * PWA affichent à l'ouverture sur iPhone est toujours une bannière de ce genre, dessinée
 * par le site. Sur Android elle ouvre en plus la vraie boîte d'installation.
 *
 * Non bloquante et refermable, elle appartient à la couche « célébrations » du D§3 : elle
 * se pose au-dessus du dock, ne recouvre jamais la ville, et s'efface sur un geste.
 */
export function createInstallBanner(): InstallBannerHandle {
  const el = document.createElement('aside');
  el.className = 'app-notice app-notice--bottom v-panel v-panel--floating';
  el.hidden = true;
  el.setAttribute('role', 'complementary');
  el.setAttribute('aria-label', t('install.title'));

  const body = document.createElement('div');
  body.className = 'app-notice__body';

  const title = document.createElement('b');
  title.textContent = t('install.title');

  const text = document.createElement('span');
  text.className = 'app-notice__text';

  body.append(title, text);

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'v-icon-btn app-notice__close';
  close.setAttribute('aria-label', t('common.close'));
  close.append(icon('close'));

  const action = createButton({
    label: t('install.action'),
    variant: 'primary',
    size: 'sm',
    onClick: () => {
      if (captured === null) return;
      void captured.prompt().finally(() => {
        captured = null;
        hide();
      });
    },
  });

  el.append(body, action.el, close);

  let timer: ReturnType<typeof setTimeout> | null = null;

  function hide(): void {
    el.hidden = true;
  }

  function dismiss(): void {
    hide();
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Sans stockage, la bannière reviendra à la prochaine session. Acceptable : le
      // joueur peut toujours la refermer, et l'entrée des réglages reste disponible.
    }
  }

  close.addEventListener('click', dismiss);

  return {
    el,
    schedule(now) {
      const state = installState();
      // Déjà installée, ou rien à proposer : la bannière ne doit même pas exister.
      if (state === 'standalone' || state === 'unavailable') return;
      if (dismissedRecently(now)) return;

      timer = setTimeout(() => {
        // On réévalue à l'échéance : l'état a pu changer pendant qu'on jouait.
        const later = installState();
        if (later === 'standalone' || later === 'unavailable') return;

        text.textContent = later === 'ios' ? t('install.ios') : t('install.why');
        action.el.hidden = later !== 'prompt';
        el.hidden = false;
      }, APPEAR_AFTER);
    },
    destroy() {
      if (timer !== null) clearTimeout(timer);
      close.removeEventListener('click', dismiss);
      el.remove();
    },
  };
}
